import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { Draggable, Droppable } from "@hello-pangea/dnd";
import { useSelector } from "react-redux";
import { List } from "react-window";

import { withFastDropAnimation } from "./selectBoxDropAnimation";
import SelectBoxItem from "./SelectBoxItem";
import { SmartlistType } from "../../api/MediaAPI.types";

import type {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DroppableProvided,
  DroppableStateSnapshot,
} from "@hello-pangea/dnd";
import type { JSX } from "react";
import type { ListImperativeAPI, RowComponentProps } from "react-window";

import type { SelectBoxDisplayItem } from "./selectBox.types";
import type { AppState } from "../../reducers";

interface ItemData {
  hasRank: boolean;
  items: SelectBoxDisplayItem[];
  seasonToSerieMap: Map<string, string>;
  source_id: string;
}

type RowProps = RowComponentProps<ItemData>;

function RowComponent({
  hasRank,
  items,
  seasonToSerieMap,
  source_id,
  index,
  style,
}: RowProps): JSX.Element | null {
  const item = items[index];

  if (!item) {
    return null;
  }

  return (
    <Draggable
      key={item.uuid}
      draggableId={item.uuid}
      index={index}
      isDragDisabled={!item.isDraggable}
    >
      {(provided): JSX.Element => (
        <SelectBoxItem
          key={`key-item-${item.uuid}`}
          hasRank={hasRank}
          item={item}
          provided={provided}
          seasonToSerieMap={seasonToSerieMap}
          source_id={source_id}
          style={style}
        />
      )}
    </Draggable>
  );
}

RowComponent.displayName = "Row";

const Row = memo(RowComponent) as typeof RowComponent;

interface VirtualListProps {
  innerRef: DroppableProvided["innerRef"];
  itemCount: number;
  itemData: ItemData;
  height: number;
}

function VirtualList({
  innerRef,
  itemCount,
  itemData,
  height,
}: VirtualListProps): JSX.Element {
  const [listInstance, setListInstance] = useState<ListImperativeAPI | null>(
    null,
  );

  const element = listInstance?.element ?? null;

  useEffect(() => {
    innerRef(element);
    return (): void => {
      innerRef(null);
    };
  }, [element, innerRef]);

  return (
    <List<ItemData>
      className="selectbox-list-container"
      listRef={setListInstance}
      rowComponent={Row}
      rowCount={itemCount}
      rowHeight={50}
      rowProps={itemData}
      style={{ height }}
    />
  );
}

interface SelectBoxItemsProps {
  hasRank?: boolean;
  items?: SelectBoxDisplayItem[];
  source_id: string;
  height: number;
}

function SelectBoxItems({
  hasRank = false,
  items = [],
  source_id,
  height,
}: SelectBoxItemsProps): JSX.Element {
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );

  const seasonToSerieMap = useMemo(() => {
    const map = new Map<string, string>();

    smartlists.data.forEach((smartlist) => {
      if (smartlist.type !== SmartlistType.SERIES) return;

      smartlist.smartlist_items.forEach((season) => {
        map.set(season.content_uuid, smartlist.uuid);
      });
    });

    return map;
  }, [smartlists.data]);

  const itemData = useMemo<ItemData>(
    () => ({
      items,
      seasonToSerieMap,
      source_id,
      hasRank,
    }),
    [items, seasonToSerieMap, source_id, hasRank],
  );

  const memoizedItem = useCallback(
    (
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
      rubric: DraggableRubric,
    ): JSX.Element => {
      const item = items[rubric.source.index];

      if (!item) {
        return <div />;
      }

      return (
        <SelectBoxItem
          hasRank={hasRank}
          isDragging={snapshot.isDragging}
          item={item}
          provided={withFastDropAnimation(provided, snapshot)}
          seasonToSerieMap={seasonToSerieMap}
          source_id={source_id}
        />
      );
    },
    [hasRank, items, seasonToSerieMap, source_id],
  );

  return (
    <div
      className="selectbox-items-container"
      id={`full-width-tabpanel-${source_id}`}
      role="tabpanel"
    >
      <Droppable
        droppableId={source_id}
        mode="virtual"
        renderClone={memoizedItem}
      >
        {(
          provided: DroppableProvided,
          snapshot: DroppableStateSnapshot,
        ): JSX.Element => {
          // Room for an item dragged in from the other list.
          const itemCount = snapshot.isUsingPlaceholder
            ? items.length + 1
            : items.length;

          return (
            <VirtualList
              height={height}
              innerRef={provided.innerRef}
              itemCount={itemCount}
              itemData={itemData}
            />
          );
        }}
      </Droppable>
    </div>
  );
}

export default memo(SelectBoxItems);
